/**
 * ╔══════════════════════════════════════════════════════════╗
 * ║              GAMESTORE — CENTRALIZED API LAYER           ║
 * ║                                                          ║
 * ║  All data access goes through this module.               ║
 * ║  To switch from mock → real API:                         ║
 * ║    1. Set MOCK_MODE = false                              ║
 * ║    2. Set API_BASE to your gateway URL                   ║
 * ║    3. Implement the fetch() calls in each method         ║
 * ╚══════════════════════════════════════════════════════════╝
 */

import type { Game, GameFilters, PaginatedResponse } from "./types";
import { MOCK_GAMES } from "../data/mockGames";
import { API_CONFIG, ERROR_MESSAGES } from "../config/constants";

// ── Internal helpers ──────────────────────────────────────
async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
  
  try {
    const res = await fetch(`${API_CONFIG.BASE_URL}${path}`, {
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      ...init,
    });
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      throw new Error(`${ERROR_MESSAGES.NETWORK} (${res.status})`);
    }
    return res.json() as Promise<T>;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

function applyFilters(games: Game[], filters: GameFilters): Game[] {
  let g = [...games];
  if (filters.genre && filters.genre !== "All")
    g = g.filter((x) => x.genre.includes(filters.genre as Game["genre"][number]));
  if (filters.platform && filters.platform !== "All")
    g = g.filter((x) => x.platform.includes(filters.platform as Game["platform"][number]));
  if (filters.price === "Free") g = g.filter((x) => x.price === 0);
  if (filters.price === "Paid") g = g.filter((x) => x.price > 0);
  if (filters.price === "On Sale")
    g = g.filter((x) => x.price < x.originalPrice && x.originalPrice > 0);
  if (filters.search) {
    const q = filters.search.toLowerCase();
    g = g.filter(
      (x) =>
        x.title.toLowerCase().includes(q) ||
        x.developer.toLowerCase().includes(q) ||
        x.tags.some((t) => t.includes(q))
    );
  }
  return g;
}

function applySort(games: Game[], sort: GameFilters["sort"]): Game[] {
  return [...games].sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "reviews") return b.reviewCount - a.reviewCount;
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    if (sort === "newest")
      return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
    return b.rating - a.rating;
  });
}

// ── Public API ────────────────────────────────────────────

export const gameService = {
  /** Fetch all featured games for the hero carousel */
  async getFeatured(): Promise<Game[]> {
    if (API_CONFIG.MOCK_MODE) return MOCK_GAMES.filter((g) => g.isFeatured);
    return apiFetch<Game[]>("/games/featured");
  },

  /** Fetch paginated + filtered game catalog (GS-202, GS-203, GS-205) */
  async getAll(filters: GameFilters = {}): Promise<PaginatedResponse<Game>> {
    if (API_CONFIG.MOCK_MODE) {
      const filtered = applyFilters(MOCK_GAMES, filters);
      const sorted = applySort(filtered, filters.sort ?? "rating");
      return { data: sorted, total: sorted.length, page: 1, pageSize: sorted.length };
    }
    const params = new URLSearchParams(filters as Record<string, string>);
    return apiFetch<PaginatedResponse<Game>>(`/games?${params}`);
  },

  /** Fetch a single game by ID (GS-301) */
  async getById(id: string): Promise<Game | null> {
    if (API_CONFIG.MOCK_MODE) return MOCK_GAMES.find((g) => g.id === id) ?? null;
    return apiFetch<Game>(`/games/${id}`);
  },

  /** Fetch newest releases (GS-206) */
  async getNewReleases(limit = 8): Promise<Game[]> {
    if (API_CONFIG.MOCK_MODE)
      return applySort(
        MOCK_GAMES.filter((g) => g.isNew),
        "newest"
      ).slice(0, limit);
    return apiFetch<Game[]>(`/games?sort=newest&limit=${limit}`);
  },

  /** Fetch top-rated games (GS-206) */
  async getTopRated(limit = 8): Promise<Game[]> {
    if (API_CONFIG.MOCK_MODE)
      return applySort(
        MOCK_GAMES.filter((g) => g.isTopRated),
        "rating"
      ).slice(0, limit);
    return apiFetch<Game[]>(`/games?sort=rating&limit=${limit}`);
  },

  /** Search games by query (GS-203) */
  async search(query: string): Promise<Game[]> {
    if (API_CONFIG.MOCK_MODE) return applyFilters(MOCK_GAMES, { search: query });
    return apiFetch<Game[]>(`/games/search?q=${encodeURIComponent(query)}`);
  },
};

// ── Wishlist service (GS-307) ─────────────────────────────
export const wishlistService = {
  async add(gameId: string): Promise<void> {
    if (API_CONFIG.MOCK_MODE) return;
    await apiFetch(`/wishlist/${gameId}`, { method: "POST" });
  },
  async remove(gameId: string): Promise<void> {
    if (API_CONFIG.MOCK_MODE) return;
    await apiFetch(`/wishlist/${gameId}`, { method: "DELETE" });
  },
  async getAll(): Promise<string[]> {
    if (API_CONFIG.MOCK_MODE) return [];
    return apiFetch<string[]>("/wishlist");
  },
};

// ── Auth service (GS-E1) ─────────────────────────────────
export const authService = {
  async login(email: string, password: string) {
    if (API_CONFIG.MOCK_MODE) return { token: "mock-jwt", user: { id: "u1", email, name: "Demo User", role: "User" as const } };
    return apiFetch("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
  },
  
  async loginWithGoogle(googleIdToken: string) {
    if (API_CONFIG.MOCK_MODE) {
      return {
        token: "mock-jwt",
        user: {
          id: "u-google-mock",
          email: "user@gmail.com",
          name: "Google User",
          role: "User" as const,
          avatarUrl: "https://lh3.googleusercontent.com/a/default-user=s96-c"
        }
      };
    }
    return apiFetch("/auth/login-google", {
      method: "POST",
      body: JSON.stringify({ googleIdToken })
    });
  },

  async loginWithProvider(provider: "google" | "apple" | "facebook") {
    if (API_CONFIG.MOCK_MODE) return { token: "mock-jwt", provider };
    window.location.href = `${API_CONFIG.BASE_URL}/auth/oauth/${provider}`;
  },

  async logout() {
    if (API_CONFIG.MOCK_MODE) return;
    await apiFetch("/auth/logout", { method: "POST" });
  },

  async me() {
    if (API_CONFIG.MOCK_MODE) return null;
    return apiFetch("/auth/me");
  },
};

// ── Billing service stubs (GS-E4) ─────────────────────────
export const billingService = {
  async purchase(gameId: string, iapItemId?: string) {
    if (API_CONFIG.MOCK_MODE) return { success: true, orderId: `mock-${Date.now()}` };
    return apiFetch("/billing/purchase", {
      method: "POST",
      body: JSON.stringify({ gameId, iapItemId }),
    });
  },
  async getPurchaseHistory() {
    if (API_CONFIG.MOCK_MODE) return [];
    return apiFetch("/billing/history");
  },
};

// ── Review service stubs (GS-E7) ─────────────────────────
export const reviewService = {
  async submit(gameId: string, rating: number, text: string) {
    if (API_CONFIG.MOCK_MODE) return { success: true };
    return apiFetch(`/games/${gameId}/reviews`, {
      method: "POST",
      body: JSON.stringify({ rating, text }),
    });
  },
  async report(reviewId: string) {
    if (API_CONFIG.MOCK_MODE) return { success: true };
    return apiFetch(`/reviews/${reviewId}/report`, { method: "POST" });
  },
};
